import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ScenarioProfileSetting, ScenarioSuiteProfile } from '../../api/api';
import { withInlineCode } from '../../utils/inlineCode';

export type PreparationState = 'ready' | 'changes' | 'unknown' | 'none';

// Whether the environment already holds what a profile needs. The API leaves `matches` undefined when it could
// not read the environment, and an unknown row must not be drawn as a correct one. 'none' is a profile that
// needs nothing at all, which has no badge and no details to open.
export const preparationState = (
    profile: ScenarioSuiteProfile,
    knowsEnvironment: boolean
): PreparationState => {
    const rows = profile.configuration ?? [];

    if (rows.length === 0 && (profile.manualSteps ?? []).length === 0) {
        return 'none';
    }

    if (!knowsEnvironment || rows.length === 0) {
        return 'unknown';
    }

    return rows.every((row) => row.matches === true) ? 'ready' : 'changes';
};

const rowsToChange = (profile: ScenarioSuiteProfile) =>
    (profile.configuration ?? []).filter((row) => row.matches !== true).length;

// A feature that is off only because the source environment has no row for it. A profile that starts from another
// environment states every known feature, and most of them are this; they are folded away so the few that say
// what the profile is about stay readable.
const isSourceDefault = (row: ScenarioProfileSetting) =>
    row.origin === 'source-default' && row.required === 'false';

const describeOrigin = (row: ScenarioProfileSetting, source?: string) => {
    switch (row.origin) {
        case 'source':
            return source;
        case 'source-default':
            return `${source}, no row`;
        case 'suite':
            return 'suite';
        default:
            return undefined;
    }
};

/**
 * What the runner will do to the environment for this profile, in one pill.
 *
 * Information rather than a warning: a run started here writes the profile's rows itself and asks every function
 * app to re-read them, so a row that differs is a row that is about to change, not one somebody has to fix.
 */
export const PreparationBadge = ({
    profile,
    knowsEnvironment,
}: {
    profile: ScenarioSuiteProfile;
    knowsEnvironment: boolean;
}) => {
    const state = preparationState(profile, knowsEnvironment);

    if (state === 'none') {
        return null;
    }

    const toChange = rowsToChange(profile);

    return (
        <span
            className={`arl-prep-badge arl-prep-badge--${state}`}
            title="The connector configuration this profile needs, compared with what the environment holds now"
        >
            <FontAwesomeIcon
                icon={
                    state === 'ready'
                        ? 'circle-check'
                        : state === 'changes'
                        ? 'rotate'
                        : 'circle-question'
                }
            />
            {state === 'ready'
                ? 'Already set'
                : state === 'changes'
                ? `Changes ${toChange} setting${toChange === 1 ? '' : 's'}`
                : 'Current values unknown'}
        </span>
    );
};

export interface IProfilePreparationProps {
    profile: ScenarioSuiteProfile;
    // False when the environment's own configuration could not be read, so nothing is claimed about it.
    knowsEnvironment: boolean;
}

/**
 * The connector configuration a profile writes, row by row, folded away by default.
 *
 * Kept for reference: somebody reading a failed run wants to know which flags were on, and a run from the
 * command line with --assume-configured only checks these rows, so there somebody does have to set them by hand
 * and needs the table, the partition key and the row key rather than the name the suite uses.
 */
export const ProfilePreparation = ({
    profile,
    knowsEnvironment,
}: IProfilePreparationProps) => {
    const [open, setOpen] = useState(false);
    const [showDefaults, setShowDefaults] = useState(false);
    const rows = profile.configuration ?? [];
    const manual = profile.manualSteps ?? [];
    const source = profile.featuresFrom;
    const folded = rows.filter(isSourceDefault);
    const shown = showDefaults
        ? rows
        : rows.filter((row) => !isSourceDefault(row));

    if (rows.length === 0 && manual.length === 0) {
        return null;
    }

    const toChange = rowsToChange(profile);

    return (
        <div className="arl-prep">
            <button
                type="button"
                className="arl-prep__toggle"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
            >
                <FontAwesomeIcon icon={open ? 'angle-down' : 'angle-right'} />
                Configuration: {rows.length} setting
                {rows.length === 1 ? '' : 's'}
                {knowsEnvironment && toChange > 0
                    ? `, ${toChange} will change`
                    : ''}
                {manual.length > 0
                    ? `, ${manual.length} to arrange by hand`
                    : ''}
            </button>

            {open && (
                <div className="arl-prep__body">
                    <p className="arl-prep__note">
                        The run writes these rows itself when it reaches this
                        profile and has every function app re-read them, so
                        nothing has to be changed by hand. A feature or setting
                        this profile does not name gets the value the
                        environment held before the run, not the one a previous
                        profile left behind, and when the run ends - also when
                        it is stopped - every row is put back as it was.
                    </p>

                    {source && (
                        <p className="arl-prep__note">
                            Feature flags are read from <code>{source}</code>{' '}
                            when the run starts
                            {profile.featuresReadAtUtc
                                ? ` (this list: ${new Date(
                                      profile.featuresReadAtUtc
                                  ).toLocaleString()})`
                                : ''}
                            . A feature without a row there is off, as the
                            connector reads it, and the suite&apos;s own flags go
                            on top. Settings are never copied from{' '}
                            <code>{source}</code>.
                        </p>
                    )}

                    {profile.featuresProblem && (
                        <p className="arl-prep__note arl-prep__note--problem">
                            <FontAwesomeIcon icon="triangle-exclamation" />{' '}
                            {profile.featuresProblem}
                        </p>
                    )}

                    {rows.length > 0 && (
                        <table className="arl-prep__table">
                            <thead>
                                <tr>
                                    <th>Setting</th>
                                    {source && <th>From</th>}
                                    <th>Becomes</th>
                                    {knowsEnvironment && <th>Is now</th>}
                                    <th>Where</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map((row) => (
                                    <tr
                                        key={`${row.kind}-${row.rowKey}`}
                                        className={
                                            row.matches === false
                                                ? 'arl-prep__row--changes'
                                                : undefined
                                        }
                                    >
                                        <td>{row.name}</td>
                                        {source && (
                                            <td className="arl-prep__from">
                                                {describeOrigin(row, source)}
                                            </td>
                                        )}
                                        <td>
                                            <code>{row.required}</code>
                                        </td>
                                        {knowsEnvironment && (
                                            <td>
                                                <code>{row.current}</code>
                                            </td>
                                        )}
                                        <td className="arl-prep__where">
                                            {row.table} / {row.partitionKey} /{' '}
                                            {row.rowKey}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {folded.length > 0 && (
                        <button
                            type="button"
                            className="arl-link-btn"
                            onClick={() => setShowDefaults(!showDefaults)}
                        >
                            {showDefaults
                                ? `Hide the ${folded.length} features that are off because ${source} has no row`
                                : `Show ${folded.length} more features, off because ${source} has no row`}
                        </button>
                    )}

                    {manual.length > 0 && (
                        <>
                            <p className="arl-prep__note">
                                Outside the connector's own tables, to arrange
                                by hand:
                            </p>
                            <ul className="arl-prep__manual">
                                {manual.map((step) => (
                                    <li key={step}>{withInlineCode(step)}</li>
                                ))}
                            </ul>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};
